/**
 * shell/useEdgeSwipe.ts —— **边缘手势**（壳规范 §1bis·三）。
 *
 * 规范逐字：**手势** —— 左缘右滑＝呼出左栏；右缘左滑＝呼出右栏 sheet；下拉＝刷新（**可选**）；
 *          长按＝上下文菜单（**可选**）；**不劫持**纵向滚动（横向手势仅用于抽屉/sheet）。
 * 纪律：只在**屏幕边缘**起手（`edge` px 内）且横向位移占优时生效；纵向滚动不被劫持；
 *      手势只是**额外汇入路**，所有功能均有真按钮入口（**无 hover-only／无手势-only**）。
 */
import React, { useRef } from "react";

export interface EdgeSwipeOptions {
  onOpenSidebar?: () => void;
  onOpenRightbar?: () => void;
  /** 边缘带宽（px；起手点须落在带内） */
  edge?: number;
  /** 触发阈值（px） */
  threshold?: number;
}

export interface EdgeSwipeHandlers {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchEnd: (event: React.TouchEvent) => void;
}

const DEFAULT_EDGE = 24;
const DEFAULT_THRESHOLD = 48;
/** 纵向容差：纵向位移超过此值 ⇒ 视为滚动，不认作边缘手势 */
const VERTICAL_TOLERANCE = 32;

export function useEdgeSwipe(options: EdgeSwipeOptions): EdgeSwipeHandlers {
  const start = useRef<{ x: number; y: number; edge: "left" | "right" } | null>(null);
  const edge = options.edge ?? DEFAULT_EDGE;
  const threshold = options.threshold ?? DEFAULT_THRESHOLD;

  const onTouchStart = (event: React.TouchEvent): void => {
    const touch = event.touches?.[0];
    if (!touch) return;
    const width = typeof window === "undefined" ? 0 : window.innerWidth;
    if (touch.clientX <= edge) start.current = { x: touch.clientX, y: touch.clientY, edge: "left" };
    else if (width > 0 && touch.clientX >= width - edge) start.current = { x: touch.clientX, y: touch.clientY, edge: "right" };
    else start.current = null;
  };

  const onTouchEnd = (event: React.TouchEvent): void => {
    const origin = start.current;
    start.current = null;
    if (!origin) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const dx = touch.clientX - origin.x;
    const dy = Math.abs(touch.clientY - origin.y);
    if (dy > VERTICAL_TOLERANCE) return;              // 纵向滚动不被劫持
    if (Math.abs(dx) < threshold) return;
    if (origin.edge === "left" && dx > 0) options.onOpenSidebar?.();
    if (origin.edge === "right" && dx < 0) options.onOpenRightbar?.();
  };

  return { onTouchStart, onTouchEnd };
}
